import { connect } from "react-redux";
import styled, { keyframes } from "styled-components";
import { format } from "date-fns";
import { Briefcase, GraduationCap, Cake, Ruler, MapPin } from "@phosphor-icons/react";
import { setSelectedProfile, getProfileContacts } from "../../store/actions";
import { EDUCATION } from "../../helpers/dbFields";

const ProfileBox = ({ profile, index = 0, searchResults, setSelectedProfile, getProfileContacts }) => {
  const dummyPhotoURL =
    profile.gender === "MALE"
      ? "https://firebasestorage.googleapis.com/v0/b/vinayaka-matrimony.appspot.com/o/Male.jpeg?alt=media&token=9d4771bc-745d-4a25-9148-a3f79c880cdb"
      : "https://firebasestorage.googleapis.com/v0/b/vinayaka-matrimony.appspot.com/o/Female.jpeg?alt=media&token=cb4d3f60-c9ea-461a-9c95-269412c7e3c7";

  const profilePic = profile.photos?.[0] || dummyPhotoURL;

  const formattedDob = profile.dob?.toDate ? format(profile.dob.toDate(), "dd-MM-yyyy") : "N/A";

  const handleViewProfile = () => {
    const selectedProfile = searchResults.find(p => p.uid === profile.uid);
    if (selectedProfile) {
      setSelectedProfile(selectedProfile);
      getProfileContacts(selectedProfile, "OTHER");
    }
  };

  return (
    <Card style={{ "--i": Math.min(index, 8) }}>
      <ImageWrapper>
        <img src={profilePic} alt={profile.display_name || profile.full_name || "Profile photo"} loading="lazy" />
      </ImageWrapper>
      <Info>
        <Name>{profile.display_name || profile.full_name || "Name not available"}</Name>
        <IdText>{profile.vnkId || profile.user_name || "ID unavailable"}</IdText>
        <Facts>
          <li><Briefcase size={16} /> {profile.job_name || "Profession not specified"}</li>
          <li><GraduationCap size={16} /> {EDUCATION[profile.education] || "Education not specified"}</li>
          <li><Cake size={16} /> DOB: {formattedDob}</li>
          <li><Ruler size={16} /> {profile.height || "Height N/A"}</li>
          <li><MapPin size={16} /> {profile.city || "City N/A"}</li>
        </Facts>
        <ViewLink type="button" onClick={handleViewProfile}>View Profile</ViewLink>
      </Info>
    </Card>
  );
};

const enter = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: none; }
`;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: var(--radius-card);
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  animation: ${enter} 0.7s var(--ease-out) both;
  animation-delay: calc(var(--i) * 60ms);
  transition:
    transform 0.45s var(--ease-out),
    box-shadow 0.45s var(--ease-out);

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-lg);
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ImageWrapper = styled.div`
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: var(--surface-tint);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.8s var(--ease-out);
  }
`;

const IdText = styled.div`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--coral);
  margin-bottom: 10px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px 18px 18px;
`;

const Name = styled.h4`
  font-size: 19px;
  margin: 0 0 2px;
`;

const Facts = styled.ul`
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: grid;
  gap: 6px;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--text-muted);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex: none;
    color: var(--coral);
  }

  @media (max-width: 520px) {
    li {
      font-size: 13px;
    }
  }
`;

const ViewLink = styled.button`
  margin-top: auto;
  min-height: 42px;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--indigo);
  color: var(--butter);
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.25s ease,
    transform 0.3s var(--ease-out);

  &:hover {
    background: var(--navy);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const mapStateToProps = (state) => ({
  searchResults: state.profileState.searchResults,
});

const mapDispatchToProps = (dispatch) => ({
  setSelectedProfile: (selectedProfile) => dispatch(setSelectedProfile(selectedProfile)),
  getProfileContacts: (selectedProfile, who) => dispatch(getProfileContacts(selectedProfile, who)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileBox);
